import { useState, useEffect } from "react";
import "./Navbar.css";

const navLinks = [
  { label: "Home",     href: "#home",     icon: "🏠", toothType: "incisor",  desc: "Start here" },
  { label: "About",    href: "#about",    icon: "👨‍⚕️", toothType: "canine",   desc: "Our doctor" },
  { label: "Services", href: "#services", icon: "✨", toothType: "premolar", desc: "What we do" },
  { label: "Our Team", href: "#team",     icon: "👥", toothType: "molar",    desc: "Meet us"    },
  { label: "Contact",  href: "#contact",  icon: "📞", toothType: "incisor",  desc: "Reach out"  },
];

const toothPaths = {
  incisor:  "M8 2 C5 2 3 4 3 7 L4 22 C4 24 6 26 8 26 C10 26 12 24 12 22 L13 7 C13 4 11 2 8 2Z",
  canine:   "M8 2 C4 2 2 5 2 9 L3 19 C3 22 5 26 8 26 C11 26 13 22 13 19 L14 9 C14 5 12 2 8 2Z",
  premolar: "M4 5 C3 3 2 2 2 5 L2 15 C2 19 4 24 7 26 C8 27 9 27 10 26 C13 24 14 19 14 15 L14 5 C14 2 13 3 12 5 L10 9 C9 11 7 11 6 9Z",
  molar:    "M3 6 C2 4 2 3 3 3 L5 3 C6 3 7 5 8 6 C9 5 10 3 11 3 L13 3 C14 3 14 4 13 6 L12 15 C12 20 10 25 8 25 C6 25 4 20 4 15Z",
};

function ToothIcon({ type, active, hovered }) {
  return (
    <svg className="tooth-svg" width="20" height="28" viewBox="0 0 16 28" fill="none">
      <path
        d={toothPaths[type]}
        className={`tooth-path ${active ? "tooth-active" : hovered ? "tooth-hovered" : ""}`}
      />
      <ellipse
        cx="6" cy="7" rx="2.2" ry="1.2"
        className={`tooth-shine ${active || hovered ? "shine-visible" : ""}`}
      />
    </svg>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [active, setActive]       = useState("#home");
  const [hovered, setHovered]     = useState(null);
  const [sparkMap, setSparkMap]   = useState({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const triggerSparks = (href) => {
    setHovered(href);
    setSparkMap(prev => ({
      ...prev,
      [href]: Array.from({ length: 4 }, (_, i) => ({
        id: Date.now() + i,
        x: 20 + Math.random() * 60,
        y: 10 + Math.random() * 50,
        angle: Math.random() * 360,
        delay: i * 80,
      }))
    }));
  };

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      {/* colourful toothpaste-stripe top accent */}
      <div className="paste-stripe">
        {["#0a7c6e","#12a090","#5dcaa5","#12a090","#0a7c6e","#065a50","#0a7c6e","#12a090"].map((c,i) => (
          <div key={i} className="paste-seg" style={{ background: c }} />
        ))}
      </div>

      <div className="navbar-inner">
        {/* ── Logo ── */}
        <a href="#home" className="navbar-logo" onClick={() => setActive("#home")}>
          <div className="logo-icon">
            <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
              <path d="M14 3C10 3 7 6.5 7 10.5c0 2 .8 3.8 2 5L14 25l5-9.5c1.2-1.2 2-3 2-5C21 6.5 18 3 14 3z" fill="#fff" fillOpacity=".95"/>
              <ellipse cx="11" cy="9" rx="2.5" ry="1.4" fill="white" fillOpacity=".45"/>
            </svg>
          </div>
          <div className="logo-text">
            <span className="logo-name">Krishna Dental</span>
            <span className="logo-sub">& Implant Centre</span>
          </div>
        </a>

        {/* ── Nav links ── */}
        <ul className={`navbar-links ${menuOpen ? "open" : ""}`}>
          {navLinks.map((link) => {
            const isActive  = active  === link.href;
            const isHovered = hovered === link.href;
            const sparks    = sparkMap[link.href] || [];

            return (
              <li key={link.label} className="nav-item">
                <a
                  href={link.href}
                  className={`nav-link ${isActive ? "is-active" : ""}`}
                  onMouseEnter={() => triggerSparks(link.href)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => { setActive(link.href); setMenuOpen(false); }}
                >
                  {/* Tooth */}
                  <div className="tooth-wrap">
                    <ToothIcon type={link.toothType} active={isActive} hovered={isHovered} />

                    {/* Sparkle burst */}
                    {isHovered && sparks.map(s => (
                      <span
                        key={s.id}
                        className="spark"
                        style={{
                          left: `${s.x}%`,
                          top:  `${s.y}%`,
                          "--angle": `${s.angle}deg`,
                          animationDelay: `${s.delay}ms`,
                        }}
                      />
                    ))}

                    {/* Mint-fresh ripple on active */}
                    {isActive && <span className="mint-ripple" />}
                  </div>

                  <span className="nav-label">{link.label}</span>

                  {/* Floating tooltip */}
                  <div className="nav-tooltip">
                    <span>{link.icon}</span>&nbsp;{link.desc}
                  </div>
                </a>
              </li>
            );
          })}

          {/* Book Now CTA */}
          <li className="nav-cta-wrap">
            <a href="#contact" className="nav-cta" onClick={() => { setActive("#contact"); setMenuOpen(false); }}>
              <span className="cta-ring" />
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2.2"/>
                <path d="M8 2v4M16 2v4M3 10h18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="2.2" fill="currentColor"/>
              </svg>
              Book Now
            </a>
          </li>
        </ul>

        {/* Hamburger */}
        <button className={`hamburger ${menuOpen ? "open" : ""}`} onClick={() => setMenuOpen(m => !m)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </div>

      {/* Frosted shine underline when scrolled */}
      <div className={`shine-line ${scrolled ? "show" : ""}`} />
    </nav>
  );
}
